import { AddressAutocompleteInput, formatAddress } from '../../components/common/AddressAutocompleteInput';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stepper,
  Step,
  StepIndicator,
  Typography,
  RadioGroup,
  Radio,
  Input,
  Textarea,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  FormLabel,
  Grid,
  LinearProgress,
} from '@mui/joy';
import {
  Buildings as BusinessIcon,
  Palette as PaletteIcon,
  Sparkle as AIIcon,
  CheckCircle as CheckIcon,
  Phone as PhoneIcon,
} from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useOrganizationDefaults } from '../../hooks/useOrganizationDefaults';

interface GenerationStatus {
  task_id: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  progress: number;
  current_step: string;
  total_pages?: number;
  pages_created?: number;
  site_id?: number;
  photos_imported?: number;
  error?: string;
  message?: string;
}

interface ServiceArea {
  city: string;
  state: string;
}

interface WizardData {
  // Step 1: Business Info
  businessType: 'residential' | 'commercial' | 'both';
  services: string[];
  serviceAreas: ServiceArea[];
  yearsInBusiness?: number;
  
  // Step 2: Theme & Branding
  theme: string;
  primaryColor?: string;
  accentColor?: string;
  
  // Step 3: Content Generation
  tone: 'professional' | 'friendly' | 'urgent' | 'educational';
  keywords: string[];
  specialOffers?: string;
  
  // Step 4: Contact Info
  phone: string;
  email: string;
  address: string;
  businessHours: string;
}

const PRESET_SERVICES = [
  'Roof Repair',
  'Roof Replacement',
  'Emergency Services',
  'Roof Inspection',
  'Gutter Installation',
  'Storm Damage Repair',
  'Commercial Roofing',
  'Residential Roofing',
  'Metal Roofing',
  'Shingle Roofing',
  'Flat Roofing',
  'Roof Maintenance',
];

const THEMES = [
  { key: 'atlas', name: 'Atlas Professional', description: 'Clean, modern design perfect for established contractors' },
  { key: 'bold', name: 'Bold Impact', description: 'Strong visual presence for companies that stand out' },
  { key: 'classic', name: 'Classic Trust', description: 'Traditional design that builds confidence' },
  { key: 'modern', name: 'Modern Edge', description: 'Cutting-edge design for innovative contractors' },
];

export const SetupWizard: React.FC = () => {
  const navigate = useNavigate();
  const { defaults, isLoading: loadingDefaults } = useOrganizationDefaults();
  const [activeStep, setActiveStep] = useState(0);
  const [wizardData, setWizardData] = useState<WizardData>({
    businessType: 'residential',
    services: [],
    serviceAreas: [],
    theme: 'atlas',
    tone: 'professional',
    keywords: [],
    phone: '',
    email: '',
    address: '',
    businessHours: '',
  });
  const [serviceAreaCity, setServiceAreaCity] = useState('');
  const [serviceAreaState, setServiceAreaState] = useState('');
  const [keywordInput, setKeywordInput] = useState('');

  // Generation task tracking
  const [generationStatus, setGenerationStatus] = useState<GenerationStatus | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Apply smart defaults when loaded
  useEffect(() => {
    if (defaults) {
      setWizardData(prev => ({
        ...prev,
        businessType: defaults.businessType || prev.businessType,
        services: defaults.services.length > 0 ? defaults.services : prev.services,
        serviceAreas: defaults.serviceAreas.length > 0 ? defaults.serviceAreas : prev.serviceAreas,
        yearsInBusiness: defaults.yearsInBusiness || prev.yearsInBusiness,
        phone: defaults.phone || prev.phone,
        email: defaults.email || prev.email,
        address: defaults.address || prev.address,
        businessHours: defaults.businessHours || prev.businessHours,
        keywords: [
          `${defaults.name} roofing`,
          'roof repair near me',
          'roofing contractor',
          ...prev.keywords
        ].filter(Boolean).slice(0, 5),
      }));
    }
  }, [defaults]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Poll for task status
  const pollTaskStatus = useCallback(async (taskId: string) => {
    try {
      const response = await api.get(`/api/cms/generate-website/${taskId}/status/`);
      const status = response.data as GenerationStatus;
      setGenerationStatus(status);

      if (status.status === 'completed' && status.site_id) {
        // Stop polling and navigate
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        setIsGenerating(false);
        navigate(`/cms/sites/${status.site_id}/pages`);
      } else if (status.status === 'failed') {
        // Stop polling and show error
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        setIsGenerating(false);
        setGenerationError(status.error || 'Website generation failed');
      }
    } catch (error) {
      console.error('Failed to poll task status:', error);
    }
  }, [navigate]);

  // Start generation
  const startGeneration = useCallback(async (data: WizardData) => {
    setIsGenerating(true);
    setGenerationError(null);
    setGenerationStatus(null);

    try {
      const response = await api.post('/api/cms/generate-website/', data);
      const responseData = response.data as {
        task_id: string;
        status?: 'pending' | 'in_progress' | 'completed' | 'failed';
        progress?: number;
        current_step?: string;
      };

      // Set initial status
      setGenerationStatus({
        task_id: responseData.task_id,
        status: responseData.status || 'pending',
        progress: responseData.progress || 0,
        current_step: responseData.current_step || 'Starting...',
      });

      // Start polling for status
      pollingRef.current = setInterval(() => {
        pollTaskStatus(responseData.task_id);
      }, 2000);

    } catch (error: any) {
      setIsGenerating(false);

      // If site already exists (409), redirect to it immediately
      if (error?.response?.status === 409 && error?.response?.data?.site_id) {
        navigate(`/cms/sites/${error.response.data.site_id}/pages`);
        return;
      }

      const errorMessage = error?.response?.data?.error || 'Failed to start website generation';
      setGenerationError(errorMessage);
    }
  }, [pollTaskStatus, navigate]);

  const generateWebsiteMutation = useMutation({
    mutationFn: async (data: WizardData) => {
      await startGeneration(data);
    },
  });

  const handleNext = () => {
    if (activeStep < 3) {
      setActiveStep(activeStep + 1);
    } else {
      // Generate the website
      generateWebsiteMutation.mutate(wizardData);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const updateData = (field: keyof WizardData, value: any) => {
    setWizardData({ ...wizardData, [field]: value });
  };

  const addServiceArea = () => {
    if (serviceAreaCity.trim() && serviceAreaState.trim()) {
      updateData('serviceAreas', [
        ...wizardData.serviceAreas,
        { city: serviceAreaCity.trim(), state: serviceAreaState.trim().toUpperCase() }
      ]);
      setServiceAreaCity('');
      setServiceAreaState('');
    }
  };

  const addKeyword = () => {
    if (keywordInput.trim()) {
      updateData('keywords', [...wizardData.keywords, keywordInput.trim()]);
      setKeywordInput('');
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography level="h3" sx={{ mb: 3 }}>
              Tell us about your roofing business
            </Typography>

            <FormControl sx={{ mb: 3 }}>
              <FormLabel>What type of roofing do you specialize in?</FormLabel>
              <RadioGroup
                value={wizardData.businessType}
                onChange={(e) => updateData('businessType', e.target.value)}
              >
                <Radio value="residential" label="Residential Roofing" />
                <Radio value="commercial" label="Commercial Roofing" />
                <Radio value="both" label="Both Residential & Commercial" />
              </RadioGroup>
            </FormControl>

            <FormControl sx={{ mb: 3 }}>
              <FormLabel>Select your services (choose all that apply)</FormLabel>
              <Grid container spacing={1}>
                {PRESET_SERVICES.map((service) => (
                  <Grid xs={6} sm={4} key={service}>
                    <Chip
                      variant={wizardData.services.includes(service) ? 'solid' : 'outlined'}
                      onClick={() => {
                        const newServices = wizardData.services.includes(service)
                          ? wizardData.services.filter(s => s !== service)
                          : [...wizardData.services, service];
                        updateData('services', newServices);
                      }}
                      sx={{ width: '100%', justifyContent: 'flex-start' }}
                    >
                      {service}
                    </Chip>
                  </Grid>
                ))}
              </Grid>
            </FormControl>

            <FormControl sx={{ mb: 3 }}>
              <FormLabel>Service Areas (cities/regions you serve)</FormLabel>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Input
                  placeholder="City"
                  value={serviceAreaCity}
                  onChange={(e) => setServiceAreaCity(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addServiceArea())}
                  sx={{ flex: 2 }}
                />
                <Input
                  placeholder="State"
                  value={serviceAreaState}
                  onChange={(e) => setServiceAreaState(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addServiceArea())}
                  slotProps={{ input: { maxLength: 2 } }}
                  sx={{ flex: 1, maxWidth: 80 }}
                />
                <Button onClick={addServiceArea}>Add</Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {wizardData.serviceAreas.map((area, index) => (
                  <Chip
                    key={index}
                    endDecorator={<span onClick={() => updateData('serviceAreas', wizardData.serviceAreas.filter((_, i) => i !== index))}>×</span>}
                  >
                    {area.city}, {area.state}
                  </Chip>
                ))}
              </Box>
            </FormControl>

            <FormControl>
              <FormLabel>Years in business (optional)</FormLabel>
              <Input
                type="number"
                placeholder="e.g., 15"
                value={wizardData.yearsInBusiness || ''}
                onChange={(e) => updateData('yearsInBusiness', parseInt(e.target.value) || undefined)}
              />
            </FormControl>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography level="h3" sx={{ mb: 3 }}>
              Choose your website style
            </Typography>

            <Grid container spacing={2}>
              {THEMES.map((theme) => (
                <Grid xs={12} sm={6} key={theme.key}>
                  <Card
                    variant={wizardData.theme === theme.key ? 'soft' : 'outlined'}
                    color={wizardData.theme === theme.key ? 'primary' : 'neutral'}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => updateData('theme', theme.key)}
                  >
                    <CardContent>
                      <Typography level="title-lg">{theme.name}</Typography>
                      <Typography level="body-sm">{theme.description}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Alert sx={{ mt: 3 }} startDecorator={<PaletteIcon />}>
              Your brand colors will be automatically extracted from your existing branding.
              You can customize these later in the site settings.
            </Alert>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography level="h3" sx={{ mb: 3 }}>
              AI Content Generation Settings
            </Typography>

            <FormControl sx={{ mb: 3 }}>
              <FormLabel>What tone should your website content have?</FormLabel>
              <RadioGroup
                value={wizardData.tone}
                onChange={(e) => updateData('tone', e.target.value)}
              >
                <Radio value="professional" label="Professional - Build trust with expertise" />
                <Radio value="friendly" label="Friendly - Approachable and warm" />
                <Radio value="urgent" label="Urgent - Emphasize immediate action" />
                <Radio value="educational" label="Educational - Inform and guide customers" />
              </RadioGroup>
            </FormControl>

            <FormControl sx={{ mb: 3 }}>
              <FormLabel>SEO Keywords (what customers search for)</FormLabel>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Input
                  placeholder="e.g., roof repair near me, emergency roofing"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                  sx={{ flex: 1 }}
                />
                <Button onClick={addKeyword}>Add</Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {wizardData.keywords.map((keyword, index) => (
                  <Chip
                    key={index}
                    endDecorator={<span onClick={() => updateData('keywords', wizardData.keywords.filter((_, i) => i !== index))}>×</span>}
                  >
                    {keyword}
                  </Chip>
                ))}
              </Box>
            </FormControl>

            <FormControl>
              <FormLabel>Current special offers (optional)</FormLabel>
              <Textarea
                placeholder="e.g., 10% off all roof replacements this month, Free inspection with any repair"
                value={wizardData.specialOffers || ''}
                onChange={(e) => updateData('specialOffers', e.target.value)}
                minRows={2}
              />
            </FormControl>

            <Alert sx={{ mt: 3 }} startDecorator={<AIIcon />}>
              Our AI will generate professional, SEO-optimized content for your entire website
              based on these settings. You can edit everything later.
            </Alert>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography level="h3" sx={{ mb: 3 }}>
              Contact Information
            </Typography>

            <FormControl sx={{ mb: 3 }}>
              <FormLabel>Business Phone Number</FormLabel>
              <Input
                startDecorator={<PhoneIcon />}
                placeholder="(555) 123-4567"
                value={wizardData.phone}
                onChange={(e) => updateData('phone', e.target.value)}
                required
              />
            </FormControl>

            <FormControl sx={{ mb: 3 }}>
              <FormLabel>Business Email</FormLabel>
              <Input
                type="email"
                placeholder="info@yourroofingcompany.com"
                value={wizardData.email}
                onChange={(e) => updateData('email', e.target.value)}
                required
              />
            </FormControl>

            <FormControl sx={{ mb: 3 }}>
              <FormLabel>Business Address</FormLabel>
              <AddressAutocompleteInput
                value={wizardData.address}
                onChange={(value) => updateData('address', value)}
                onAddressSelect={(parsed) => updateData('address', formatAddress(parsed))}
                required
              />
            </FormControl>

            <FormControl>
              <FormLabel>Business Hours</FormLabel>
              <Textarea
                placeholder="Monday-Friday: 8:00 AM - 6:00 PM&#10;Saturday: 9:00 AM - 4:00 PM&#10;Sunday: Emergency calls only"
                value={wizardData.businessHours}
                onChange={(e) => updateData('businessHours', e.target.value)}
                minRows={3}
              />
            </FormControl>
          </Box>
        );

      default:
        return null;
    }
  };

  if (isGenerating) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', gap: 3 }}>
        <CircularProgress size="lg" />
        <Typography level="h3">Creating your website...</Typography>

        {generationStatus && (
          <Box sx={{ width: '100%', maxWidth: 500 }}>
            <LinearProgress
              determinate
              value={generationStatus.progress}
              sx={{ mb: 2 }}
            />
            <Typography level="body-md" sx={{ textAlign: 'center', mb: 1 }}>
              {generationStatus.current_step}
            </Typography>
            {generationStatus.total_pages && generationStatus.total_pages > 0 && (
              <Typography level="body-sm" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                {generationStatus.pages_created || 0} of {generationStatus.total_pages} pages created
              </Typography>
            )}
          </Box>
        )}

        {!generationStatus && (
          <Typography level="body-md" sx={{ textAlign: 'center', maxWidth: 500 }}>
            We're generating your complete website with AI-optimized content,
            professional design, and SEO settings. This usually takes 30-60 seconds.
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography level="h1" sx={{ mb: 1 }}>
        Create Your Roofing Website in Minutes
      </Typography>
      <Typography level="body-lg" sx={{ mb: 4, color: 'text.secondary' }}>
        Just answer a few questions and we'll build a professional, SEO-optimized website for you.
      </Typography>

      <Stepper sx={{ mb: 4 }}>
        <Step
          indicator={
            <StepIndicator variant={activeStep >= 0 ? 'solid' : 'outlined'} color={activeStep > 0 ? 'success' : 'primary'}>
              <BusinessIcon />
            </StepIndicator>
          }
        >
          Business Info
        </Step>
        <Step
          indicator={
            <StepIndicator variant={activeStep >= 1 ? 'solid' : 'outlined'} color={activeStep > 1 ? 'success' : 'primary'}>
              <PaletteIcon />
            </StepIndicator>
          }
        >
          Design
        </Step>
        <Step
          indicator={
            <StepIndicator variant={activeStep >= 2 ? 'solid' : 'outlined'} color={activeStep > 2 ? 'success' : 'primary'}>
              <AIIcon />
            </StepIndicator>
          }
        >
          Content
        </Step>
        <Step
          indicator={
            <StepIndicator variant={activeStep >= 3 ? 'solid' : 'outlined'} color={activeStep > 3 ? 'success' : 'primary'}>
              <PhoneIcon />
            </StepIndicator>
          }
        >
          Contact
        </Step>
      </Stepper>

      <Card>
        <CardContent sx={{ p: 4 }}>
          {renderStepContent()}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0}
            >
              Back
            </Button>
            <Button
              variant="solid"
              onClick={handleNext}
              endDecorator={activeStep === 3 ? <CheckIcon /> : undefined}
              disabled={
                (activeStep === 0 && (wizardData.services.length === 0 || wizardData.serviceAreas.length === 0)) ||
                (activeStep === 3 && (!wizardData.phone || !wizardData.email || !wizardData.address))
              }
            >
              {activeStep === 3 ? 'Create My Website' : 'Next'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {generationError && (
        <Alert color="danger" sx={{ mt: 2 }}>
          {generationError}
        </Alert>
      )}
    </Box>
  );
};
